import { PERMISSIONS as P } from '@/lib/permissions'
import type { AuditRecordRef, AuditTrailLink, AuditTrailRow } from '@/types/audit'
import type { StorageAuditEntry } from '@/types/storage'
import { auditStorageNode } from '@/lib/auditTrail'
import { describeStorageAuditEvent } from '@/lib/storageAudit'
import { entityTarget, submissionTarget, subjectTarget, type AuditLinkTarget } from '@/lib/auditLinks'

/**
 * Which audit trail a reference in the audit log opens (z8nrz7dq6q).
 *
 * A hyperlink in an audit trail opens the linked item's OWN trail in a pop-up,
 * not the item's page: a reviewer following "what happened to this patient,
 * this bag, this freezer slot, this centrifuge" stays on the record they were
 * reviewing. The item's page is still one click away ("Open item").
 *
 * Pure — no React, no network — so the mapping is unit-tested.
 */
export type TrailKind = 'patient' | 'product' | 'storage' | 'equipment' | 'submission' | 'lot'

export interface TrailSource {
  kind: TrailKind
  id: string
  /** What the reader saw on the link — the MRN, the DIN, the position. */
  label: string
  /** The item's own page, for "Open item". Null where the item has no page. */
  openItem: AuditLinkTarget | null
}

/**
 * Who may open each kind of trail — ANY one is enough. Mirrors the permission
 * on each trail's endpoint, so a link never opens a pop-up that then 403s.
 */
export const TRAIL_PERMISSIONS: Record<TrailKind, readonly string[]> = {
  patient: [P.auditRead],
  submission: [P.auditRead],
  equipment: [P.auditRead],
  product: [P.productsView],
  storage: [P.inventoryView],
  // An inventory lot's trail is its storage history — the storage endpoint's.
  lot: [P.inventoryView],
}

const KIND_NOUN: Record<TrailKind, string> = {
  patient: 'Patient',
  product: 'Product',
  storage: 'Storage',
  equipment: 'Equipment',
  submission: 'Form',
  lot: 'Lot',
}

/** "Audit trail — Patient MRN-007". */
export function trailTitle(source: TrailSource): string {
  return `Audit trail — ${KIND_NOUN[source.kind]} ${source.label}`
}

/** A short crumb for the pop-up's back trail. */
export function trailCrumb(source: TrailSource): string {
  return `${KIND_NOUN[source.kind]} ${source.label}`
}

export function sameSource(a: TrailSource | null | undefined, b: TrailSource | null | undefined): boolean {
  return !!a && !!b && a.kind === b.kind && a.id === b.id
}

/** The MRN column. Only a patient MRN has a trail; a KIT or WO key does not. */
export function subjectSource(
  row: Pick<AuditTrailRow, 'subjectKey' | 'subjectKeyType' | 'procedureId'>,
): TrailSource | null {
  if (!row.subjectKey || row.subjectKeyType !== 'mrn') return null
  return { kind: 'patient', id: row.subjectKey, label: row.subjectKey, openItem: subjectTarget(row) }
}

/** The DIN and Product code columns. Both open the same product's trail. */
export function productSource(row: Pick<AuditTrailRow, 'productId' | 'din' | 'productCode'>): TrailSource | null {
  if (!row.productId) return null
  return {
    kind: 'product',
    id: row.productId,
    label: row.din || row.productCode || row.productId,
    openItem: entityTarget('product', row.productId),
  }
}

/** The Storage node column. */
export function storageSource(row: AuditTrailRow): TrailSource | null {
  const { id, label } = auditStorageNode(row)
  if (!id) return null
  return { kind: 'storage', id, label: label || id, openItem: entityTarget('storage_node', id) }
}

/** The Submission column. */
export function submissionSource(row: Pick<AuditTrailRow, 'submissionId'>): TrailSource | null {
  if (!row.submissionId) return null
  return {
    kind: 'submission',
    id: row.submissionId,
    label: row.submissionId.slice(0, 8),
    openItem: submissionTarget(row),
  }
}

/**
 * The record an Audit Trail Center row came from (z8nrz7dq6q), as the trail it
 * opens. Material, pick list, transfer, zone, profile, group and procedure
 * records have no trail of their own and render as text.
 */
export function recordSource(record: AuditRecordRef | null): TrailSource | null {
  if (!record) return null
  const { id, label } = record
  switch (record.kind) {
    case 'product':
      return { kind: 'product', id, label, openItem: entityTarget('product', id) }
    case 'storage':
      return { kind: 'storage', id, label, openItem: entityTarget('storage_node', id) }
    case 'equipment':
      return { kind: 'equipment', id, label, openItem: null }
    case 'lot':
      return { kind: 'lot', id, label, openItem: null }
    case 'submission':
      return { kind: 'submission', id, label, openItem: submissionTarget({ submissionId: id }) }
    case 'patient':
      return { kind: 'patient', id, label, openItem: null }
    default:
      return null
  }
}

/**
 * A `links` entry in the Related column. Products, storage positions and
 * equipment have trails; a form definition, a procedure template or a process
 * step is followed to its page as before.
 */
export function linkSource(link: AuditTrailLink): TrailSource | null {
  const label = link.entityLabel || link.entityId
  switch (link.entityType) {
    case 'product':
      return { kind: 'product', id: link.entityId, label, openItem: entityTarget('product', link.entityId) }
    case 'storage_node':
      return { kind: 'storage', id: link.entityId, label, openItem: entityTarget('storage_node', link.entityId) }
    case 'equipment':
      // No per-unit page exists to open.
      return { kind: 'equipment', id: link.entityId, label, openItem: null }
    case 'inventory_lot':
      // A lot has no page of its own; its trail is where it has been stored.
      return { kind: 'lot', id: link.entityId, label, openItem: null }
    default:
      return null
  }
}

/**
 * A storage position's own trail row, in the shape every other trail uses, so
 * the pop-up renders all of them with one table. The storage endpoint keeps
 * its own shape; this is a read-side projection of it.
 */
export function storageEntryToAuditRow(entry: StorageAuditEntry, positionLabel?: string): AuditTrailRow {
  const described = describeStorageAuditEvent(entry)
  const product = productOf(entry)
  const lotNumber = valueOf(entry, 'lotNumber')
  return {
    id: entry.id,
    source: 'storage',
    transactionId: entry.id,
    transactionType:
      entry.eventType === 'created' ? 'insert' : entry.eventType === 'deleted' ? 'delete' : 'update',
    changeType: 'general',
    // A product placed in or taken out of the slot: its DIN links to the
    // product's own trail, so a reviewer can cross from storage to product.
    productId: product.productId,
    subjectKey: null,
    subjectKeyType: null,
    din: product.din,
    productCode: null,
    submissionId: null,
    procedureId: null,
    formDefinitionId: null,
    itemChanged: described.label,
    oldValue: entry.oldValue,
    newValue: entry.newValue,
    eventType: entry.eventType,
    reason: entry.reason,
    description: described.detail ? `${described.label}: ${described.detail}` : described.label,
    dynamicData: null,
    storageNodeId: entry.nodeId,
    // A lot's trail spans several positions, so each row names its own.
    storageNodeLabel: positionLabel ?? valueOf(entry, 'path') ?? entry.nodeId,
    changedBy: entry.changedBy,
    changedByActor: entry.changedByActor,
    actorKind: entry.changedBy === 'system' ? 'system' : 'user',
    changedAt: entry.changedAt,
    // An inventory lot put away or taken out: link its own trail (z8nrz7dq6q).
    links: entry.lotId
      ? [
          {
            id: `lot:${entry.lotId}`,
            entityType: 'inventory_lot',
            entityId: entry.lotId,
            entityLabel: lotNumber ?? entry.lotId,
          },
        ]
      : [],
  }
}

/** A string value from the event's new value, else its old one. */
function valueOf(entry: Pick<StorageAuditEntry, 'oldValue' | 'newValue'>, key: string): string | null {
  for (const v of [entry.newValue, entry.oldValue]) {
    const found = v && typeof v === 'object' ? (v as Record<string, unknown>)[key] : undefined
    if (typeof found === 'string' && found) return found
  }
  return null
}

/**
 * The product a storage event moved, read from the event's own values: the
 * storage service records `productId` and `din` on every product placement
 * (the new value when stored, the old value when taken out). Null for a lot or
 * a structural change.
 */
function productOf(entry: Pick<StorageAuditEntry, 'oldValue' | 'newValue'>): {
  productId: string | null
  din: string | null
} {
  for (const v of [entry.newValue, entry.oldValue]) {
    if (v && typeof v === 'object') {
      const { productId, din } = v as { productId?: unknown; din?: unknown }
      if (typeof productId === 'string' && productId) {
        return { productId, din: typeof din === 'string' ? din : null }
      }
    }
  }
  return { productId: null, din: null }
}
